import { colors, Typography } from '@ui-components/neo.tax';
import React, { ReactElement, useState } from 'react';

import { VirtualizedList } from '../virtualized-list';
import { getLinkedPullRequestsHeight } from './LinkedPullRequests';
import { MemoizedTicketRow, TicketRowTicketType } from './TicketRow';
import { EmptyStateInner, EmptyStateOuter } from './tickets-list.styled';

type TicketsListProps = {
  tickets: TicketRowTicketType[];
  selectedIds: Set<string>;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  loadMore?: () => void | PromiseLike<void>;
  isLoading: boolean;
  Footer?: () => ReactElement;
  loadMoreRowFromBottomThreshold?: number;
  isGroupingSelected?: boolean;
  disableSelection?: boolean;
};

const TICKET_ROW_HEIGHT = 48;
const LIST_FOOTER_HEIGHT = 32;

// The list is given its full height, so the container around it does the scrolling.
const getListHeight = (tickets: TicketRowTicketType[], expandedIds: Set<string>): number =>
  tickets.reduce(
    (height, ticket) =>
      height +
      TICKET_ROW_HEIGHT +
      (expandedIds.has(ticket.id) ? getLinkedPullRequestsHeight(ticket.linkedPullRequests?.length ?? 0) : 0),
    LIST_FOOTER_HEIGHT,
  );

export const TicketsList = ({
  Footer,
  disableSelection = false,
  isGroupingSelected = false,
  isLoading,
  loadMore,
  loadMoreRowFromBottomThreshold = 10,
  selectedIds,
  setSelectedIds,
  tickets,
}: TicketsListProps): ReactElement => {
  // Held here rather than in each row because the virtualized list unmounts rows scrolled out of view.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  return tickets.length === 0 && !isLoading ? (
    <EmptyStateOuter direction="column" justifyContent="center">
      <EmptyStateInner direction="column" justifyContent="center">
        <Typography color={colors.gray[300]} fontSize="sm" fontWeight="semibold" textAlign="center">
          No matching tickets
        </Typography>
      </EmptyStateInner>
    </EmptyStateOuter>
  ) : (
    <VirtualizedList
      data={tickets ?? []}
      isLoading={isLoading}
      loadMore={loadMore}
      height={getListHeight(tickets ?? [], expandedIds)}
      MemoizedRowComponent={({ item }) => (
        <MemoizedTicketRow
          key={item.id}
          item={{
            ...item,
            selected: selectedIds.has(item.id),
            onSelectedChange: disableSelection
              ? undefined
              : (selected: boolean): void => {
                  setSelectedIds((selectedIds) => {
                    const newSelectedIds = new Set(selectedIds);
                    if (selected) {
                      newSelectedIds.add(item.id);
                    } else {
                      newSelectedIds.delete(item.id);
                    }
                    return newSelectedIds;
                  });
                },
            disabled: disableSelection,
            isGroupingSelected,
            isExpanded: expandedIds.has(item.id),
            onExpandedChange: (expanded: boolean): void => {
              setExpandedIds((expandedIds) => {
                const newExpandedIds = new Set(expandedIds);
                if (expanded) {
                  newExpandedIds.add(item.id);
                } else {
                  newExpandedIds.delete(item.id);
                }
                return newExpandedIds;
              });
            },
          }}
        />
      )}
      Footer={Footer}
      loadMoreRowFromBottomThreshold={loadMoreRowFromBottomThreshold}
    />
  );
};
